import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, emptyProgress, type Progress } from '../lib/api';
import { demoComplete, demoNote, demoToggle, loadDemoProgress, saveDemoNote } from '../lib/demo';
import { useAuth } from './AuthContext';

type ProgressContextValue = {
  progress: Progress;
  loading: boolean;
  celebration: number | null;
  clearCelebration: () => void;
  toggleTask: (day: number, taskId: string, done: boolean) => Promise<void>;
  completeDay: (day: number) => Promise<void>;
  getNote: (day: number) => Promise<string>;
  saveNote: (day: number, body: string) => Promise<void>;
};

const ProgressContext = createContext<ProgressContextValue | null>(null);

export function ProgressProvider({ children }: { children: ReactNode }) {
  const { user, isDemo } = useAuth();
  const [progress, setProgress] = useState<Progress>(emptyProgress());
  const [loading, setLoading] = useState(true);
  const [celebration, setCelebration] = useState<number | null>(null);

  const hydrate = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try { setProgress(isDemo ? loadDemoProgress() : await api.progress()); }
    finally { setLoading(false); }
  }, [user, isDemo]);

  useEffect(() => { void hydrate(); }, [hydrate]);

  async function toggleTask(day: number, taskId: string, done: boolean) {
    if (isDemo) setProgress((current) => demoToggle(current, day, taskId, done));
    else setProgress(await api.toggleTask(day, taskId, done));
  }

  async function completeDay(day: number) {
    if (progress.days[day]?.status !== 'completed') setCelebration(day);
    if (isDemo) setProgress((current) => demoComplete(current, day));
    else setProgress(await api.completeDay(day));
  }

  async function getNote(day: number) {
    if (isDemo) return demoNote(day);
    return (await api.getNote(day)).note.body;
  }

  async function saveNote(day: number, body: string) {
    if (isDemo) saveDemoNote(day, body);
    else await api.saveNote(day, body);
  }

  return (
    <ProgressContext.Provider value={{ progress, loading, celebration, clearCelebration: () => setCelebration(null), toggleTask, completeDay, getNote, saveNote }}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const context = useContext(ProgressContext);
  if (!context) throw new Error('useProgress must be used inside ProgressProvider');
  return context;
}

